import React, { useEffect, useRef, useCallback } from 'react';
import { formatDate, formatUrl } from '../../common/Formatter';
import { PaymentDialogProps } from '../types/Payment';
import { useTranslations } from '../../localization/useTranslations';
import {
  getPaymentType,
  isPaymentFailed,
  getPaymentStatusMessage,
  formatPaymentAmount,
  getPaymentMethod,
  getDeliveryAddressFromPayment,
  getInvoiceAddressFromPayment,
} from '../helpers/PaymentUtils';

const PaymentDialog = ({ payment, onClose }: PaymentDialogProps) => {
  // Get translations based on the country
  const translations = useTranslations();

  // Ref for the dialog element
  const dialogRef = useRef<HTMLDivElement>(null);

  // Get payment details
  const statusMessage = getPaymentStatusMessage(payment, translations);
  const amount = formatPaymentAmount(payment);
  const paymentMethod = getPaymentMethod(payment, translations);
  const deliveryAddress = getDeliveryAddressFromPayment(payment);
  const invoiceAddress = getInvoiceAddressFromPayment(payment);
  const myAccountSlug = window.myAccountPageSlug || 'my-account';

  // Close dialog when clicking outside
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    },
    [onClose]
  );

  // Add event listener for clicking outside the dialog
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Render the PaymentDialog component
  return (
    <div id={`order-details-modal-${payment.id}`} className="order-details-modal modal modal--large" ref={dialogRef}>
      <div className="order-details-modal__wrapper modal__wrapper">
        {/* Header */}
        <div className="order-details-modal__header modal__header">
          <div className="order-details-modal__header-title-wrapper">
            <h5 className="order-details-modal__title modal__title">
              <span>{translations?.orderLabels?.orderDetailsTitle || 'Order'} </span>
              <span className="order-details-modal__title-number">{payment.id}</span>
            </h5>
            <span className="order-details-modal__status basic basic-14" id="modal-status">
              {statusMessage}
            </span>
            <button className="close-button" onClick={onClose}></button>
          </div>
        </div>

        {/* Content */}
        <div className="order-details-modal__content modal__content">
          {/* Details */}
          <div className="order-details-modal__details-wrapper row">
            {/* Left details */}
            <div className="col-lg-6 order-details-modal__details">
              {/* Created date */}
              <div className="order-details-modal__date-created order-details-modal__single-data">
                <span className="order-details-modal__label basic basic-16">
                  {translations?.orderLabels?.createdDate || 'Created date'}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-date-created">
                  {formatDate(payment.created_at)}
                </span>
              </div>

              {/* Payment amount */}
              <div className="order-details-modal__sum order-details-modal__single-data">
                <span className="order-details-modal__label basic basic-16">
                  {translations?.orderLabels?.sum || 'Sum'}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-sum">
                  {amount}
                </span>
              </div>
            </div>

            {/* Right details */}
            <div className="col-lg-6 order-details-modal__details">
              {/* Payment method */}
              <div className="order-details-modal__payment-method order-details-modal__single-data">
                <span className="order-details-modal__label basic basic-16">
                  {translations?.orderLabels?.paymentMethod || 'Payment method'}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-payment-method">
                  {paymentMethod}
                </span>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="order-details-modal__items-wrapper row">
            <div className="col-lg-12 order-details-modal__details">
              <div className="order-details-modal__label-wrapper">
                <span className="order-details-modal__label basic basic-16">
                  {translations?.orderLabels?.items || 'Items'}
                </span>
                <span className="order-details-modal__label basic basic-16 prices-label">
                  {translations?.orderLabels?.sum || 'Sum'}
                </span>
              </div>
              <div className="order-details-modal__items">
                {/* Display product names and prices if available */}
                {payment.products && payment.products.length > 0 ? (
                  payment.products.map((product, index) => (
                    <div key={index} className="order-details-modal__item">
                      <span className="order-details-modal__data basic basic-16" id="modal-item">
                        {product.name}
                      </span>
                      <span className="order-details-modal__data basic basic-16" id="modal-price">
                        {formatPaymentAmount(product)}
                      </span>
                    </div>
                  ))
                ) : (
                  // Display subscription label if no products
                  <span className="order-details-modal__data basic basic-16" id="modal-item">
                    {payment.subscription?.label ?? '-'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Address details */}
          <div className="order-details-modal__address-details row">
            {/* Left delivery address */}
            <div className="col-lg-6">
              <div className="order-details-modal__delivery-address order-details-modal__single-data">
                <span className="order-details-modal__label basic basic-16">
                  {translations?.orderLabels?.deliveryAddress || 'Delivery address'}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-delivery-name">
                  {deliveryAddress?.first_name}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-delivery-address">
                  {deliveryAddress?.address}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-delivery-state">
                  {deliveryAddress?.city} {deliveryAddress?.zip}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-delivery-country">
                  {deliveryAddress?.country}
                </span>
              </div>
            </div>

            {/* Right billing address */}
            <div className="col-lg-6">
              <div className="order-details-modal__billing-address order-details-modal__single-data">
                <span className="order-details-modal__label basic basic-16">
                  {translations?.orderLabels?.billingAddress || 'Billing address'}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-billing-name">
                  {invoiceAddress.first_name} {invoiceAddress.last_name}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-billing-address">
                  {invoiceAddress.address}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-billing-state">
                  {invoiceAddress.city} {invoiceAddress.zip}
                </span>
                <span className="order-details-modal__data basic basic-16" id="modal-billing-country">
                  {invoiceAddress.country}
                </span>
              </div>
            </div>
          </div>

          {/* Empty div */}
          <div></div>
        </div>

        {/* Footer */}
        <div className="order-details-modal__footer modal__footer">
          {/* Invoice details */}
          <div className="order-details-modal__invoice-wrapper">
            {/* Display invoice link if available */}
            {payment.invoice_url ? (
              <a
                href={formatUrl(payment.invoice_url)}
                className="order-details-modal__invoice link link--medium link--icon"
                rel="noreferrer noopenner"
                target="_blank"
              >
                {translations?.orderLabels?.invoice || 'Invoice'} {payment.id}
              </a>
            ) : (
              // Display no invoice message if payment is not failed
              <>
                {!isPaymentFailed(payment) && (
                  <div className="order-details-modal__no-invoice-wrapper">
                    <span className="order-details-modal__label basic basic-16">
                      {translations?.orderLabels?.noInvoiceLabel || "You don't have an invoice"}
                    </span>

                    {/* Display description for unavailable invoice */}
                    <div className="order-details-modal__no-invoice-description">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: window?.forbes_my_account_payments?.unavailableInvoiceDescription,
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Display retry button for failed payments */}
            {isPaymentFailed(payment) && payment.retry_url && payment.status !== 'timeout' && (
              <a
                href={payment.retry_url}
                className="order-details-modal__retry-button link link--medium"
                target="_blank"
                rel="noreferrer noopenner"
              >
                {translations?.orderLabels?.retryPayment || 'Retry payment'}
              </a>
            )}

            {/* Display "Get the codes" button for business type payments */}
            {getPaymentType(payment.sales_funnel) === 'business' ? (
              <a
                className="order-details-modal__retry-button link link--medium"
                href={`/${myAccountSlug}/#/${translations?.route?.subscriptions}/"`}
              >
                {translations?.orderLabels?.getCodesLabel || 'Get the codes'}
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDialog;
